/**
 * Rune templates a capsule can be tagged with.
 *
 * Slugs = the HIVE rune pages in landing-hive/runes/*.html. Their shared
 * "Collect →" button (landing-hive/runes/_collect-cta.js) links to
 * /login?from=runes&template=<slug>, or — when the rune exports its result —
 * opens /send?from=runes&template=<slug>&handoff=1 (lib/runeHandoff.ts); the send
 * form preselects that slug. An unknown but well-formed slug is still accepted
 * (new runes work without a change here) — see templateOptions().
 */
export const RUNE_TEMPLATES = [
  'Attribution',
  'BeeSwarm',
  'CaptureKnowledge',
  'CodeFlow',
  'CodeGraph',
  'DirectoryScanner',
  'FamilyTree',
  'Lottery',
  'Pong',
  'Slots',
  'Snake',
  'ValueFlow',
] as const;

export type RuneTemplate = (typeof RUNE_TEMPLATES)[number];

/** One line per rune: what a capsule with this template holds (shown under the template picker). */
export const RUNE_TEMPLATE_DESCRIPTIONS: Readonly<Record<RuneTemplate, string>> = Object.freeze({
  Attribution: 'Contribution rings: actors and who contributed what to a result (demo, not anchored).',
  BeeSwarm: 'A force-directed swarm: bees, queens and the alliances between them.',
  CaptureKnowledge: 'Captured knowledge: URLs, folders and archives turned into a concept graph.',
  CodeFlow: 'The architecture of a GitHub repository: dependencies, blast radius and ownership.',
  CodeGraph: 'The file dependency graph and extracted symbols of a GitHub repository.',
  DirectoryScanner: 'The structure of a local folder, shown as a radial graph.',
  FamilyTree: 'A family tree (genogram): people, parents and partners.',
  Lottery: 'A result of the Lottery draw game.',
  Pong: 'A result of a Pong game.',
  Slots: 'A result of the Slots game (no real money).',
  Snake: 'A result of a Snake game.',
  ValueFlow: 'A Sankey diagram: nodes and the values flowing between them (demo, off-chain).',
});

/** Description of a known template, or undefined for an unknown slug. */
export function templateDescription(slug: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(RUNE_TEMPLATE_DESCRIPTIONS, slug)
    ? RUNE_TEMPLATE_DESCRIPTIONS[slug as RuneTemplate]
    : undefined;
}

/** "CaptureKnowledge" → "Capture Knowledge" */
export function templateLabel(slug: string): string {
  return slug
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Known templates plus the preselected one when it is not in the list. */
export function templateOptions(selected?: string): string[] {
  const known: string[] = [...RUNE_TEMPLATES];
  if (selected && !known.includes(selected)) known.push(selected);
  return known;
}

/** Tag recorded on the capsule (vat.hive.data.capsule.tags) for a template. */
export function templateTag(slug: string): string {
  return `template:${slug}`;
}
